import i18n from "i18next";
import {initReactI18next} from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import {LanguageCode} from "./types";

// Type for the resources object i18next expects
type I18nResources = {
    [language: string]: {
        [namespace: string]: any; // You could define a more specific type if needed
    };
};

// Use Vite's import.meta.glob to find all locale files eagerly
// Adjust the path ('../locale/*.json') if your i18n.ts is located differently relative to the 'locale' folder
const locales = import.meta.glob("./locale/*.json", {eager: true});

// Prepare the resources object for i18next
const resources = Object.keys(locales).reduce((acc: I18nResources, path) => {
    // Extract language code from filename (e.g., '../locale/en.json' -> 'en')
    const langCode = path.match(/\.\/locale\/(.*)\.json$/)?.[1];

    if (langCode) {
        // Assume the default namespace is 'translation'
        // The content is available under the 'default' key when eagerly imported
        acc[langCode] = {translation: (locales[path] as any).default};
    }
    return acc;
}, {} as I18nResources);

// Your available languages and default
// Make sure the keys ('en', 'nb', 'sv', 'da') match the filenames
// generated by Lokalise in src/locale (e.g., en.json, nb.json)
const supportedLngs = [
    LanguageCode.English,
    LanguageCode.Norwegian,
    LanguageCode.Swedish,
    LanguageCode.Danish,
];
const defaultLng = LanguageCode.Norwegian; // Or your preferred default

i18n
    // Detect user language
    .use(LanguageDetector)
    // Pass the i18n instance to react-i18next.
    .use(initReactI18next)
    // Init i18next
    .init({
        supportedLngs: supportedLngs, // Explicitly list supported languages
        fallbackLng: defaultLng, // Use this if detected language is not available
        resources: resources, // Provide the loaded resources directly
        lng: localStorage.getItem("i18nextLng") || defaultLng, // Ensure a default is set on first load if detector misses
        debug: import.meta.env.DEV, // Enable debug output in development
        ns: ["translation"], // Default namespace; Lokalise usually exports into one file per language
        defaultNS: "translation",

        detection: {
            // Order and from where user language should be detected
            order: [
                "localStorage", // Check localStorage first (where i18next caches the selected language)
                "navigator", // Then check browser language
                "htmlTag", // Then check <html lang="...">
                // Add other detectors if needed (e.g., query string, cookie)
            ],
            caches: ["localStorage"], // Cache the detected language in localStorage
        },

        interpolation: {
            escapeValue: false, // React already safes from xss
        },

        react: {
            // Use suspense for loading translations (recommended)
            // Wrap your app or specific components with <Suspense fallback="...">
            useSuspense: true,
        },
    });

export default i18n;
